import { getDeviceId, getUserId } from '@amplitude/analytics-browser';

import appSettings from 'wanderu-config';
import {
    splitTestSelectors,
} from 'wanderu-ducks-split-test';
import Logger from 'wanderu-logger';
import { SnowplowService } from 'wanderu-service-analytics';
import { isLocalStorageAvailable } from 'wanderu-utils';

const logger = Logger.getLogger('clientApiService');

let cachedAuthToken;
let cachedSessionId;
let cachedAuthCredentials = {
    accessToken: undefined, // federated logins only
    email: undefined, // cognito only,
    provider: undefined,
    timestamp: undefined,
};

let onSessionChangeListener = () => undefined;
let authOperation = null;
let store;
let affiliateId: string | undefined;
let visitorId: string | undefined;

interface InitServiceParams {
    affiliateId?: string;
    visitorId?: string;
}

function initService({ affiliateId: passedAffiliateId, visitorId: passedVisitorId }: InitServiceParams) {
    affiliateId = passedAffiliateId;
    visitorId = passedVisitorId;
}

function injectStore(passedStore) {
    store = passedStore;
}

function setUserAuthTokens(authTokens) {
    if (!isLocalStorageAvailable()) {
        return;
    }

    window.localStorage.setItem('userAccessToken', JSON.stringify(authTokens));
}

function isSameAuthCreds(newAuthCreds, oldAuthCreds) {
    if (newAuthCreds.provider !== oldAuthCreds.provider) {
        return false;
    }

    return newAuthCreds.provider === 'cognito'
        ? newAuthCreds.email === oldAuthCreds.email
        : newAuthCreds.accessToken === oldAuthCreds.accessToken;
}

function restoreSession(sessionInfo) {
    const timestamp = Date.now();
    setSession({ ...sessionInfo, timestamp });
}

function setAuthCredentials({
    provider, accessToken, email, timestamp,
}) {
    cachedAuthCredentials = {
        accessToken,
        email,
        provider,
        timestamp,
    };
}

function setSession(sessionInfo) {
    const {
        accessToken,
        authToken,
        email,
        provider,
        sessionId,
        timestamp,
    } = sessionInfo;
    const {
        timestamp: prevTimestamp,
    } = cachedAuthCredentials;

    if (timestamp < prevTimestamp) {
        return;
    }

    setAuthCredentials({
        accessToken,
        email,
        provider,
        timestamp,
    });

    cachedAuthToken = authToken;
    cachedSessionId = sessionId;

    onSessionChangeListener(sessionId);
}

/* cognito provider only */
async function refreshSession(email, expiredAuthToken, preserveSessionId = false) {
    try {
        const payload = {
            email,
            meta: getSessionMeta(),
            sessionId: preserveSessionId
                ? cachedSessionId
                : undefined,
            token: expiredAuthToken,
        };

        const url = buildWapiUrl('refresh.json');

        authOperation = fetch(url, {
            body: JSON.stringify(payload),
            headers: getHeaders('', 'text/plain'),
            method: 'post',
        });

        const response = await authOperation;
        const json = await response.json();

        setSession({
            accessToken: undefined,
            authToken: json.result.token,
            email,
            provider: 'cognito',
            sessionId: json.result.sessionId,
        });

        // overwrite existing user account info in localstorage
        setUserAuthTokens({
            accessTokenExp: json.result.expDt,
            authToken: json.result.token,
            email,
            identityId: json.result.identityId,
            provider: 'cognito',
            sessionId: json.result.sessionId,
        });

        return json.result;
    } catch (ex) {
        logger.error('refreshSession', ex, {
            email,
            expiredAuthToken,
            preserveSessionId,
        });
        throw ex;
    }
}

function assertResponseHasNoErrors(response) {
    if (!response.error) {
        return;
    }

    const code = response.error.codeKey || response.error.code; // tix used error.code

    const error = new Error(code);

    error.code = code;
    error.responseBody = response;

    throw error;
}

function getHeaders(authToken = '', contentType = 'application/json') {
    const headers = {
        Accept: 'application/json, text/plain, */*',
    };

    if (authToken) {
        headers['X-TOKEN'] = authToken;
    }

    if (contentType) {
        headers['Content-Type'] = contentType;
    }

    return headers;
}

function isUnauthorized(response = {}) {
    return response.status === 401;
}

function hasAuthToken() {
    return !!cachedAuthToken;
}

async function del(url, init = {}, opts = {}) {
    const { withAuth } = opts;

    try {
        if (withAuth && !hasAuthToken()) {
            await startNewSession();
        }

        const response = await fetch(url, {
            method: 'delete',
            ...init,
            headers: {
                ...getHeaders(withAuth
                    ? cachedAuthToken
                    : '', false),
                ...init.headers,
            },
        });

        if (isUnauthorized(response)) {
            await startNewSession();
            return del(url, init, opts);
        }

        const json = await response.json();

        assertResponseHasNoErrors(json);

        return json;
    } catch (ex) {
        logger.warn('client delete error', ex, {
            init,
            resHeaders: ex?.response?.headers,
            url,
            withAuth,
        });
        throw ex;
    }
}

async function get(url, init = {}, opts = {}) {
    const { withAuth } = opts;

    try {
        if (withAuth && !hasAuthToken()) {
            await startNewSession();
        }

        const response = await fetch(url, {
            method: 'get',
            ...init,
            headers: {
                ...getHeaders(withAuth
                    ? cachedAuthToken
                    : '', false),
                ...init.headers,
            },
        });

        if (isUnauthorized(response)) {
            await startNewSession();
            return get(url, init);
        }

        const json = await response.json();

        assertResponseHasNoErrors(json);

        return json;
    } catch (ex) {
        // return the response headers for debugging
        logger.warn('client get error', ex, {
            init,
            resHeaders: ex?.response?.headers,
            url,
            withAuth,
        });
        throw ex;
    }
}

async function post(url, init = {}, opts = {}) {
    const {
        contentType,
        preserveSession,
        withAuth,
    } = opts;
    let { authViaHeader } = opts;

    try {
        if (withAuth && !hasAuthToken()) {
            await startNewSession();
        }

        if (withAuth && typeof init.body === 'string') {
            // Let's not get into a game of parsing the most likely json
            // and insert the X-TOKEN. So let's take the hit and do the OPTIONS calls
            authViaHeader = true;
        }

        if (withAuth && !authViaHeader) {
            /* eslint-disable-next-line no-param-reassign */
            init.body['X-TOKEN'] = cachedAuthToken;
        }

        const body = typeof init.body === 'string'
            ? JSON.parse(init.body)
            : init.body;

        if (url.includes('auth.json') && !body.meta && !Array.isArray(body)) {
            body.meta = getSessionMeta();
        }

        const response = await fetch(url, {
            method: 'post',
            ...init,
            body: JSON.stringify(body),
            headers: {
                ...getHeaders(withAuth && authViaHeader
                    ? cachedAuthToken
                    : '', contentType),
                ...init.headers,
            },
        });

        if (isUnauthorized(response) && !preserveSession) {
            await startNewSession();
            return post(url, init);
        }

        const json = await response.json();

        assertResponseHasNoErrors(json);

        return json;
    } catch (ex) {
        // return the post response headers for debugging
        logger.warn('client post error', ex, {
            init,
            resHeaders: ex?.response?.headers,
            url,
            withAuth,
        });
        throw ex;
    }
}

function clearCredentials() {
    cachedAuthCredentials = {
        accessToken: undefined,
        authToken: undefined,
        email: undefined,
        provider: undefined,
        sessionId: undefined,
        timestamp: undefined,
    };

    onSessionChangeListener(undefined);
}

function getSessionMeta() {
    const deviceIds = [];
    const userIds = [];

    const amplitudeDeviceId = getDeviceId();
    const amplitudeUserId = getUserId();

    if (amplitudeDeviceId) {
        deviceIds.push({
            id: amplitudeDeviceId,
            source: 'amplitude',
        });
    }
    if (amplitudeUserId) {
        userIds.push({
            id: amplitudeUserId,
            source: 'amplitude',
        });
    }

    return {
        affiliateId,
        deviceIds,
        spDomainSessionID: SnowplowService.getDomainUserInfo().domainSessionId,
        spDomainSessionIndex: SnowplowService.getDomainUserInfo().domainSessionIdx,
        spDomainUserID: SnowplowService.getDomainUserId(),
        spNetworkID: SnowplowService.getNetworkUserId(),
        userIds,
        wanderuVisitorID: visitorId,
    };
}

async function startSession(authCredentials = cachedAuthCredentials) {
    const {
        accessToken,
        email,
        password,
        provider,
        timestamp,
    } = authCredentials;

    setAuthCredentials({
        accessToken,
        email,
        provider,
    });

    const payload = {
        meta: getSessionMeta(),
    };

    if (store) {
        const splitTestOverrides = splitTestSelectors.selectTestOverrides(store.getState());
        payload.meta = {
            ...payload.meta,
            splitTestOverrides,
        };
    }

    if (provider === 'cognito') {
        payload.credentials = {
            ...appSettings.config.wapi.credentials,
            email,
            password,
            type: 'email',
        };
    } else if (!!provider && provider !== 'cognito') {
        payload.credentials = {
            ...appSettings.config.wapi.credentials,
            provider: provider,
            token: accessToken,
            type: 'federated',
        };
    } else {
        payload.credentials = {
            ...appSettings.config.wapi.credentials,
            type: 'user',
            username: 'anonymous',
        };
    }

    try {
        const url = buildWapiUrl('auth.json');

        authOperation = fetch(url, {
            body: JSON.stringify(payload),
            headers: getHeaders('', 'text/plain'),
            method: 'post',
        });

        const response = await authOperation;
        const json = await response.json();

        assertResponseHasNoErrors(json);

        setSession({
            accessToken,
            authToken: json.result.token,
            email,
            provider,
            sessionId: json.result.sessionId,
            timestamp,
        });

        return json.result;
    } catch (ex) {
        // add more data here to get better reporting
        logger.error('startNewSession', ex, {
            meta: payload.meta,
            provider,
            type: payload.credentials.type,
        });
        throw ex;
    }
}

async function startNewSession(authCredentials = cachedAuthCredentials, options = {}) {
    const {
        accessToken,
        email,
        password,
        provider,
    } = authCredentials;
    const { preserveSessionId } = options;

    const timestamp = Date.now();

    if (authOperation && isSameAuthCreds(authCredentials, cachedAuthCredentials)) {
        /* only one call to auth.json with the same credentials at a time please */
        const result = await authOperation;
        return result;
    }

    if (provider === 'cognito'
        && email === cachedAuthCredentials.email
        && !password
        && cachedAuthToken
    ) {
        authOperation = refreshSession(email, cachedAuthToken, preserveSessionId);
    } else {
        authOperation = startSession({
            accessToken,
            email,
            password,
            provider,
            timestamp,
        });
    }

    try {
        const result = await authOperation;
        return result;
    } finally {
        authOperation = null;
    }
}

function buildWapiUrl(path) { // would have put this in wapiservice, but can't have circular references
    return appSettings.config.wapi.urlRoot + path;
}

function getSessionId() {
    return cachedSessionId;
}

function onSessionChange(listener) {
    onSessionChangeListener = listener;
}

export default {
    clearCredentials,
    delete: del,
    get,
    getSessionId,
    initService,
    injectStore,
    onSessionChange,
    post,
    refreshSession,
    restoreSession,
    setSession,
    startNewSession,
};
